// frontend/src/services/apiService.ts
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import {
  withRetry,
  classifyError,
  logError,
  checkNetworkConnectivity,
  DEFAULT_RETRY_CONFIG,
  type AppError,
  type RetryConfig,
} from '../utils/errorHandling';
import type { ApiResponse, User, Test, TestResults } from '../types';

// ----------------------------------------------------------------------------
// Base URL resolution
// - Dev: prefer VITE_API_BASE_URL, else 'http://localhost:3001/api'
// - Prod: prefer VITE_API_BASE_URL if set, else same-origin '/api'
// This ensures Vite proxy (/api -> 3001) works in dev if configured, and that
// absolute base can be used without proxy.
// ----------------------------------------------------------------------------
const isDev = import.meta.env.DEV;
const ENV_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined;

// If running behind a reverse proxy in prod, keep '/api' as default.
// If local dev without proxy, point at backend port.
const API_BASE_URL = isDev
  ? (ENV_BASE || 'http://localhost:3001/api')
  : (ENV_BASE || '/api');

const DEFAULT_TIMEOUT = 10000; // 10 seconds

// ----------------------------------------------------------------------------
// Token management
// ----------------------------------------------------------------------------
const TOKEN_REFRESH_ENDPOINT = '/auth/refresh';

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

const getStoredRefreshToken = (): string | null => {
  return localStorage.getItem('refreshToken');
};

const setTokens = (accessToken: string, refreshToken?: string | null) => {
  localStorage.setItem('authToken', accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  // update activity + absolute start
  try {
    localStorage.setItem('lastActivityAt', String(Date.now()));
    if (!localStorage.getItem('absoluteSessionStart')) {
      localStorage.setItem('absoluteSessionStart', String(Date.now()));
    }
  } catch {}
};

const clearTokens = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('lastActivityAt');
  localStorage.removeItem('absoluteSessionStart');
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('user');
  localStorage.removeItem('userRole');
};

// ----------------------------------------------------------------------------
// Axios instance
// ----------------------------------------------------------------------------
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: DEFAULT_TIMEOUT,
    headers: { 'Content-Type': 'application/json' },
  });

  // Request interceptor for adding auth tokens + no-store
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
      // touch activity on each request (except health)
      if (!config.url?.includes('/health')) {
        try { localStorage.setItem('lastActivityAt', String(Date.now())); } catch {}
      }
      return config;
    },
    (error) => Promise.reject(classifyError(error))
  );

  // Response interceptor for handling token refresh on 401
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 errors (token expired) - immediate revoke
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'TOKEN_REVOKED') {
        clearTokens();
        window.location.href = '/login?reason=revoked';
        return Promise.reject(error);
      }
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue the request
          return new Promise((resolve) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(instance(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = getStoredRefreshToken();
        if (!refreshToken) {
          // No refresh token, clear tokens and redirect to login
          clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        try {
          // Attempt to refresh the token (rotation)
          const response = await axios.post(`${API_BASE_URL}${TOKEN_REFRESH_ENDPOINT}`, {
            refreshToken
          });

          if (response.data?.success && response.data?.data?.accessToken) {
            const newAccessToken = response.data.data.accessToken;
            const newRefreshToken = response.data.data.refreshToken as string | undefined;
            setTokens(newAccessToken, newRefreshToken || refreshToken);
            onTokenRefreshed(newAccessToken);
            isRefreshing = false;

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return instance(originalRequest);
          } else {
            throw new Error('Invalid refresh response');
          }
        } catch (refreshError: any) {
          isRefreshing = false;
          // Distinguish revocation vs transient
          const code = refreshError?.response?.data?.error?.code;
          const isRevoked = code === 'TOKEN_REVOKED' || code === 'INVALID_REFRESH_TOKEN' || code === 'REFRESH_TOKEN_EXPIRED';
          clearTokens();
          if (isRevoked) {
            window.location.href = '/login?reason=revoked';
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }

      // For other errors, just reject
      const appError = classifyError(error);
      logError(appError, 'API Request Failed');
      return Promise.reject(appError);
    }
  );

  return instance;
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
const toId = (id: string | number) => String(id);

// Small health throttle so callers don’t hammer the endpoint by mistake.
let lastHealthCheckAt = 0;
const HEALTH_THROTTLE_MS = 5000;

// ----------------------------------------------------------------------------
// API service class
// ----------------------------------------------------------------------------
class ApiService {
  private axiosInstance: AxiosInstance;
  private retryConfig: RetryConfig;

  constructor(retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG) {
    this.axiosInstance = createAxiosInstance();
    this.retryConfig = retryConfig;
  }

  // Generic request method with retry logic
  private async request<T>(
    config: AxiosRequestConfig,
    customRetryConfig?: Partial<RetryConfig>
  ): Promise<ApiResponse<T>> {
    const finalRetryConfig = { ...this.retryConfig, ...customRetryConfig };

    // Debug logging
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('API Request:', {
        method: config.method,
        url: config.url,
        baseURL: this.axiosInstance.defaults.baseURL,
        data: config.data,
      });
    }

    return withRetry(
      async () => {
        const response: AxiosResponse<ApiResponse<T>> = await this.axiosInstance.request(config);

        if (isDev) {
          // eslint-disable-next-line no-console
          console.log('API Response:', response.data);
        }
        return response.data;
      },
      finalRetryConfig,
      (attempt, error) => {
        // eslint-disable-next-line no-console
        console.log(`API request retry attempt ${attempt} for ${config.url}:`, (error as any)?.message);
        // eslint-disable-next-line no-console
        console.error('API Error details:', error);
      }
    );
  }

  // --------------------------------------------------------------------------
  // Health check
  // - Server exposes HEAD /api/health (preferred) and GET /api/health.
  // - Throttle to avoid resource exhaustion in dev.
  // --------------------------------------------------------------------------
  async healthCheck(): Promise<boolean> {
    const now = Date.now();
    if (now - lastHealthCheckAt < HEALTH_THROTTLE_MS) {
      return true;
    }
    lastHealthCheckAt = now;

    try {
      await this.request<void>({ method: 'HEAD', url: '/health' }, { maxAttempts: 1 });
      return true;
    } catch {
      // Some deployments might not support HEAD; fallback to GET.
      try {
        await this.request<void>({ method: 'GET', url: '/health' }, { maxAttempts: 1 });
        return true;
      } catch {
        return false;
      }
    }
  }

  // --------------------------------------------------------------------------
  // Users
  // --------------------------------------------------------------------------
  async getUsers(): Promise<ApiResponse<User[]>> {
    return this.request<User[]>({ method: 'GET', url: '/users' });
  }

  async getUserById(id: number | string): Promise<ApiResponse<User>> {
    return this.request<User>({ method: 'GET', url: `/users/${toId(id)}` });
  }

  async createUser(userData: { name: string; email: string; password: string; role?: 'ADMIN'; companyCode?: string; companyName?: string }): Promise<ApiResponse<User>> {
    return this.request<User>({ method: 'POST', url: '/users', data: userData });
  }

  async updateUser(id: number | string, userData: Partial<User>): Promise<ApiResponse<User>> {
    return this.request<User>({ method: 'PUT', url: `/users/${toId(id)}`, data: userData });
  }

  // --------------------------------------------------------------------------
  // Tests
  // --------------------------------------------------------------------------
  async createTest(testData: {
    employeeId: string;
    employeeName?: string;
    department: string;
    workShift: 'MORNING' | 'EVENING' | 'NIGHT';
    testType?: 'CHECKERBOARD' | 'LETTER';
    lastSleepDuration?: number;
    sleepQuality?: number;
    sleepinessLevel?: number;
  }): Promise<ApiResponse<Test>> {
    return this.request<Test>({ method: 'POST', url: '/tests', data: testData });
  }

  async getTestById(id: number | string): Promise<ApiResponse<Test>> {
    return this.request<Test>({ method: 'GET', url: `/tests/${toId(id)}` });
  }

  async getUserTests(
    userId: number | string,
    params?: {
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
      includeCount?: boolean;
      includeRaw?: boolean;
      testType?: 'CHECKERBOARD' | 'LETTER';
    }
  ): Promise<ApiResponse<Test[] | { tests: Test[]; total: number }>> {
    return this.request<Test[] | { tests: Test[]; total: number }>({
      method: 'GET',
      url: `/tests/user/${toId(userId)}`,
      params
    });
  }

  async getAllTests(params?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    includeCount?: boolean;
    includeRaw?: boolean;
    status?: 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'ALL';
    testType?: 'CHECKERBOARD' | 'LETTER';
  }): Promise<ApiResponse<Test[] | { tests: Test[]; total: number }>> {
    return this.request<Test[] | { tests: Test[]; total: number }>({
      method: 'GET',
      url: '/tests',
      params
    });
  }

  async getTestSummaries(params?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    includeCount?: boolean;
    testType?: 'CHECKERBOARD' | 'LETTER';
  }): Promise<ApiResponse<Test[] | { tests: Test[]; total: number }>> {
    return this.request<Test[] | { tests: Test[]; total: number }>({
      method: 'GET',
      url: '/tests/summary',
      params
    });
  }

  async getAllTestSummaries(params?: { startDate?: string; endDate?: string; testType?: 'CHECKERBOARD' | 'LETTER' }): Promise<Test[]> {
    const pageSize = 5000;
    const tests: Test[] = [];
    let offset = 0;

    while (true) {
      const response = await this.getTestSummaries({
        ...params,
        limit: pageSize,
        offset,
        includeCount: true,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to load test summaries');
      }

      const page = Array.isArray(response.data) ? response.data : response.data.tests;
      tests.push(...page);
      const total = Array.isArray(response.data) ? undefined : response.data.total;
      if (!page.length || (total !== undefined ? tests.length >= total : page.length < pageSize)) {
        return tests;
      }
      offset += page.length;
    }
  }

  // Save results for an authenticated user's test
  async submitUserTestResults(
    testId: string,
    results: {
      reactionTimes: number[];
      lapses: number;
      falseStarts: number;
      totalResponses: number;
      testDuration?: number;
    }
  ): Promise<ApiResponse<TestResults>> {
    return this.request<TestResults>(
      {
        method: 'PUT',
        url: `/tests/${encodeURIComponent(testId)}/results`,
        data: results,
      },
      { maxAttempts: 5, baseDelay: 2000 }
    );
  }


  async submitTestResults(
    testId: string,
    results: {
      reactionTimes: number[];
      lapses: number;
      falseStarts: number;
      totalResponses: number;
      testDuration?: number;
    }
  ): Promise<ApiResponse<TestResults>> {
    return this.request<TestResults>(
      {
        method: 'PUT',
        url: `/tests/${encodeURIComponent(testId)}/results`,
        data: results,
      },
      {
        // Critical operation - more retries and longer base delay
        maxAttempts: 5,
        baseDelay: 2000,
      }
    );
  }

  // --------------------------------------------------------------------------
  // Kiosk test flow (no auth, kiosk token encodes the company)
  // --------------------------------------------------------------------------
  async startKioskTest(data: {
    kioskToken: string;
    employeeId: string;
    employeeName?: string;
    department: string;
    workShift: 'MORNING' | 'EVENING' | 'NIGHT';
    testType?: 'CHECKERBOARD' | 'LETTER';
    lastSleepDuration: number;
    sleepQuality: number;
    sleepinessLevel: number;
    testDurationMinutes?: number;
  }): Promise<ApiResponse<{
    testId: string;
    employee?: { id: string; employeeId: string; name: string | null };
    company?: { id: string; name: string; code: string };
  }>> {
    return this.request<{
      testId: string;
      employee?: { id: string; employeeId: string; name: string | null };
      company?: { id: string; name: string; code: string };
    }>({
      method: 'POST',
      url: '/tests/kiosk/start',
      data
    });
  }

  async submitKioskTestResults(
    testId: string,
    data: {
      companyCode?: string;
      companyId?: string;
      employeeId: string;
      reactionTimes: number[];
      lapses: number;
      falseStarts: number;
      totalResponses: number;
      testDuration?: number;
      completedEarly?: boolean;
    }
  ): Promise<ApiResponse<Test>> {
    return this.request<Test>({
      method: 'PUT',
      url: `/tests/kiosk/${encodeURIComponent(testId)}/results`,
      data
    });
  }

  async getKioskTestResults(
    testId: string,
    params: {
      companyCode?: string;
      companyId?: string;
      employeeId: string;
    }
  ): Promise<ApiResponse<Test>> {
    return this.request<Test>({
      method: 'GET',
      url: `/tests/kiosk/${encodeURIComponent(testId)}`,
      params
    });
  }

  async getKioskToken(adminUserId: string): Promise<ApiResponse<{ token: string; expiresAt: string; usageCount: number; maxUses: number }>> {
    return this.request<{ token: string; expiresAt: string; usageCount: number; maxUses: number }>({
      method: 'GET',
      url: `/admin/${encodeURIComponent(adminUserId)}/kiosk-token`
    });
  }

  // Guest results with optional company context
  async submitGuestTestResults(
    sessionId: string,
    guestInfo: {
      name: string;
      age?: number;
      company?: string;
    },
    results: {
      reactionTimes: number[];
      lapses: number;
      falseStarts: number;
      totalResponses: number;
      averageReactionTime: number;
      testDuration: number;
    },
    companyContext?: {
      department?: string;
      source?: string;
    },
    testType?: 'CHECKERBOARD' | 'LETTER'
  ): Promise<ApiResponse<{
    id: string;
    sessionId: string;
    results: {
      averageReactionTime: number;
      medianReactionTime: number;
      majorLapses: number;
      minorLapses: number;
      totalLapses: number;
      totalFalseStarts: number;
      accuracy: number;
      completedAt: string;
    };
    message: string;
  }>> {
    return this.request(
      {
        method: 'POST',
        url: '/tests/guest/results',
        data: {
          sessionId,
          guestInfo,
          results,
          companyContext,
          testType,
          completedAt: new Date().toISOString(),
        },
      },
      {
        maxAttempts: 5,
        baseDelay: 2000,
      }
    );
  }

  // --------------------------------------------------------------------------
  // Batch operations with enhanced error handling
  // --------------------------------------------------------------------------
  async batchOperation<T>(
    operations: (() => Promise<T>)[],
    options: { continueOnError?: boolean; maxConcurrent?: number } = {}
  ): Promise<{ results: T[]; errors: AppError[] }> {
    const { continueOnError = true, maxConcurrent = 3 } = options;
    const results: T[] = [];
    const errors: AppError[] = [];

    for (let i = 0; i < operations.length; i += maxConcurrent) {
      const batch = operations.slice(i, i + maxConcurrent);

      const batchResults = await Promise.all(
        batch.map(async (operation, index) => {
          try {
            const result = await operation();
            return { success: true as const, result, index: i + index };
          } catch (error) {
            const appError = classifyError(error);
            logError(appError, `Batch operation ${i + index} failed`);
            return { success: false as const, error: appError, index: i + index };
          }
        })
      );

      for (const br of batchResults) {
        if (br.success) {
          results[br.index] = br.result as T;
        } else {
          errors.push(br.error as AppError);
          if (!continueOnError) {
            throw br.error;
          }
        }
      }
    }

    return { results, errors };
  }

  // --------------------------------------------------------------------------
  // Connection quality assessment
  // --------------------------------------------------------------------------
  async assessConnectionQuality(): Promise<{
    isOnline: boolean;
    latency: number;
    quality: 'excellent' | 'good' | 'poor' | 'offline';
  }> {
    if (!navigator.onLine) {
      return { isOnline: false, latency: -1, quality: 'offline' };
    }

    try {
      const startTime = performance.now();
      await this.healthCheck();
      const latency = performance.now() - startTime;

      let quality: 'excellent' | 'good' | 'poor' | 'offline';
      if (latency < 100) quality = 'excellent';
      else if (latency < 300) quality = 'good';
      else quality = 'poor';

      return { isOnline: true, latency, quality };
    } catch {
      return { isOnline: false, latency: -1, quality: 'offline' };
    }
  }

  // --------------------------------------------------------------------------
  // Auth
  // --------------------------------------------------------------------------
  async login(email: string, password: string): Promise<ApiResponse<User>> {
    return this.request<User>({
      method: 'POST',
      url: '/auth/login',
      data: { email, password },
      timeout: 5000,
    }, { maxAttempts: 1 });
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.request<User>({ method: 'GET', url: '/auth/me' }, { maxAttempts: 1 });
  }

  async logoutApi(refreshToken?: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>({ method: 'POST', url: '/auth/logout', data: { refreshToken } }, { maxAttempts: 1 });
  }

  async refreshTokenApi(refreshToken: string): Promise<ApiResponse<{ accessToken: string; refreshToken: string; expiresIn: string }>> {
    return this.request<{ accessToken: string; refreshToken: string; expiresIn: string }>({ method: 'POST', url: '/auth/refresh', data: { refreshToken } }, { maxAttempts: 1 });
  }

  // --------------------------------------------------------------------------
  // Admin
  // --------------------------------------------------------------------------
  async getCompanyAnalytics(adminUserId: string): Promise<ApiResponse<any>> {
    return this.request<any>({ method: 'GET', url: `/admin/${encodeURIComponent(adminUserId)}/analytics` });
  }

  async getCompanyUsers(adminUserId: string): Promise<ApiResponse<User[]>> {
    return this.request<User[]>({ method: 'GET', url: `/admin/${encodeURIComponent(adminUserId)}/users` });
  }

  async getUserTestHistory(
    adminUserId: string,
    userId: string,
    params?: { limit?: number; offset?: number; includeCount?: boolean }
  ): Promise<ApiResponse<{ user: User; tests: Test[]; total?: number }>> {
    return this.request<{ user: User; tests: Test[]; total?: number }>({
      method: 'GET',
      url: `/admin/${encodeURIComponent(adminUserId)}/users/${encodeURIComponent(userId)}/history`,
      params,
    });
  }

  async createCompanyUser(
    adminUserId: string,
    userData: { name: string; email: string; password: string; role?: 'ADMIN'; companyCode?: string; companyName?: string }
  ): Promise<ApiResponse<User>> {
    return this.request<User>({
      method: 'POST',
      url: `/admin/${encodeURIComponent(adminUserId)}/users`,
      data: userData,
    });
  }

  async updateCompanyUser(
    adminUserId: string,
    userId: string,
    userData: { name?: string; email?: string; role?: 'ADMIN' }
  ): Promise<ApiResponse<User>> {
    return this.request<User>({
      method: 'PUT',
      url: `/admin/${encodeURIComponent(adminUserId)}/users/${encodeURIComponent(userId)}`,
      data: userData,
    });
  }

  async deleteCompanyUser(
    adminUserId: string,
    userId: string
  ): Promise<ApiResponse<{ message: string; deletedUserId: string }>> {
    return this.request<{ message: string; deletedUserId: string }>({
      method: 'DELETE',
      url: `/admin/${encodeURIComponent(adminUserId)}/users/${encodeURIComponent(userId)}`,
    });
  }

  async getGuestAnalytics(
    adminUserId: string,
    timeframe: '7d' | '30d' | '90d' = '30d',
    includeAnonymous: boolean = true
  ): Promise<ApiResponse<{
    totalTests: number;
    timeframe: string;
    averagePerformance: {
      reactionTime: number;
      lapseRate: number;
      accuracy: number;
    };
    departmentBreakdown: Array<{
      department: string;
      testCount: number;
      averageReactionTime: number;
      lapseRate: number;
      averageAccuracy: number;
    }>;
    dailyTrends: Array<{
      date: string;
      testCount: number;
      averageReactionTime: number;
      averageAccuracy: number;
    }>;
    ageGroupAnalysis: Array<{
      ageGroup: string;
      testCount: number;
      averageReactionTime: number;
      averageAccuracy: number;
    }>;
    performanceDistribution: {
      excellent: number;
      good: number;
      fair: number;
      poor: number;
    };
  }>> {
    return this.request({
      method: 'GET',
      url: `/admin/${encodeURIComponent(adminUserId)}/guest-analytics`,
      params: { timeframe, includeAnonymous: includeAnonymous.toString() },
    });
  }

  async getGuestTestAnalytics(
    timeframe: '7d' | '30d' | '90d' = '30d',
    department?: string
  ): Promise<ApiResponse<{
    totalTests: number;
    timeframe: string;
    averagePerformance: {
      reactionTime: number;
      lapseRate: number;
    };
    departmentBreakdown: Array<{
      department: string;
      testCount: number;
      averageReactionTime: number;
      lapseRate: number;
    }>;
    dailyTrends: Array<{
      date: string;
      testCount: number;
      averageReactionTime: number;
    }>;
  }>> {
    return this.request({
      method: 'GET',
      url: '/tests/guest/analytics',
      params: { timeframe, department },
    });
  }

  async cleanupExpiredGuestSessions(): Promise<ApiResponse<{
    cleanedSessions: number;
    cleanedTests: number;
    cleanedResults: number;
  }>> {
    return this.request({
      method: 'DELETE',
      url: '/tests/guest/cleanup',
    });
  }

  // --------------------------------------------------------------------------
  // Config/maintenance
  // --------------------------------------------------------------------------
  updateRetryConfig(newConfig: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...newConfig };
  }

  resetInstance(): void {
    this.axiosInstance = createAxiosInstance();
  }
}

// Create and export singleton instance
export const apiService = new ApiService();

// Export class for testing or custom instances
export { ApiService };

// ----------------------------------------------------------------------------
// Convenience wrapper for ad‑hoc operations
// ----------------------------------------------------------------------------
export const withApiErrorHandling = async <T>(
  operation: () => Promise<T>,
  fallbackValue?: T,
  customErrorMessage?: string
): Promise<T | null> => {
  try {
    return await operation();
  } catch (error) {
    const appError = classifyError(error);
    if (customErrorMessage) {
      // eslint-disable-next-line no-console
      console.error(customErrorMessage, appError.userMessage);
    }
    logError(appError, 'API operation failed');
    return fallbackValue ?? null;
  }
};

// ----------------------------------------------------------------------------
// Network status monitoring
// ----------------------------------------------------------------------------
export class NetworkStatusMonitor {
  private listeners: ((isOnline: boolean) => void)[] = [];
  private checkInterval: number | null = null;
  private lastKnownStatus = navigator.onLine;

  private checkIntervalMs: number;

  constructor(checkIntervalMs = 30000) {
    this.checkIntervalMs = checkIntervalMs;
    this.setupEventListeners();
    this.startPeriodicCheck();
  }

  private setupEventListeners(): void {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  private handleOnline = (): void => {
    this.updateStatus(true);
  };

  private handleOffline = (): void => {
    this.updateStatus(false);
  };

  private updateStatus(isOnline: boolean): void {
    if (this.lastKnownStatus !== isOnline) {
      this.lastKnownStatus = isOnline;
      this.notifyListeners(isOnline);
    }
  }

  private notifyListeners(isOnline: boolean): void {
    this.listeners.forEach((listener) => {
      try {
        listener(isOnline);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in network status listener:', error);
      }
    });
  }

  private startPeriodicCheck(): void {
    // Use window.setInterval to get a numeric id compatible with browsers
    this.checkInterval = window.setInterval(async () => {
      const isConnected = await checkNetworkConnectivity();
      this.updateStatus(isConnected);
    }, this.checkIntervalMs);
  }

  addListener(listener: (isOnline: boolean) => void): () => void {
    this.listeners.push(listener);
    // Unsubscribe
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  getCurrentStatus(): boolean {
    return this.lastKnownStatus;
  }

  destroy(): void {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.listeners = [];
  }
}

// Export singleton network monitor
export const networkMonitor = new NetworkStatusMonitor();
